<template>
  <div class="mobile-container">
    <div class="search">
      <input
        type="text"
        placeholder="CIQ ID or Ticker"
        v-model="query"
        @keyup.enter="addPortfolio"
      />
      <button class="icon-btn" @click="addPortfolio" title="Add">+</button>
      <button class="icon-btn" @click="refresh" title="Refresh">↻</button>
    </div>

    <div v-if="loading" class="loader">Loading...</div>
    <div v-if="error" class="error">{{ error }}</div>

    <!-- Portfolio Cards -->
    <div class="portfolio-section" v-if="portfolio.length > 0">
      <h3 class="section-title">Portfolio Credit Score</h3>
      <div class="portfolio-cards">
        <div v-for="(o, index) in portfolio" :key="index" class="portfolio-card">
          <div class="card-header">
            <span class="company-name">{{ o.name || 'Loading...' }}</span>
            <button class="remove-btn" @click="removePortfolio(index)">✕</button>
          </div>
          <div class="card-body">
            <div class="info-row">
              <span class="label">ID/Ticker</span>
              <span class="value">{{ o.ticker && o.ticker != 'Data Unavailable' ? o.ticker : o.companyId }}</span>
            </div>
            <div class="info-row">
              <span class="label">Type</span>
              <span class="value">{{ o.type || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="label">Currency</span>
              <span class="value">{{ o.currency || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="label">Industry</span>
              <span class="value">{{ o.industry || '-' }}</span>
            </div>
            <div class="info-row highlight">
              <span class="label">Credit Score</span>
              <span class="value score">{{ o.creditScore || '-' }}</span>
            </div>
            <div class="pd-row">
              <div class="pd-item">
                <span class="pd-label">1yr PD</span>
                <span class="pd-value">{{ isNaN(o.pd1yr) ? '-' : vueNumberFormat(o.pd1yr, { suffix: '%', precision: 4 }) }}</span>
              </div>
              <div class="pd-item">
                <span class="pd-label">3yr PD</span>
                <span class="pd-value">{{ isNaN(o.pd3yr) ? '-' : vueNumberFormat(o.pd3yr, { suffix: '%', precision: 4 }) }}</span>
              </div>
              <div class="pd-item">
                <span class="pd-label">5yr PD</span>
                <span class="pd-value">{{ isNaN(o.pd5yr) ? '-' : vueNumberFormat(o.pd5yr, { suffix: '%', precision: 4 }) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="charts-section" v-if="portfolio.length > 0">
      <!-- Chart 1: Revenue & Growth -->
      <div v-if="chartLoaded1" class="chart-card">
        <div class="chart-title">3-Year Sales & Growth</div>
        <div class="chart-body">
          <Bar
            :chart-options="chartOptions1"
            :chart-data="chartData1"
            :height="220"
          />
        </div>
      </div>

      <!-- Chart 2: Carbon Emission -->
      <div v-if="chartLoaded2" class="chart-card">
        <div class="chart-title">Carbon Emission (tonnes CO2e)</div>
        <div class="chart-body">
          <Bar
            :chart-options="chartOptions2"
            :chart-data="chartData2"
            :height="220"
          />
        </div>
      </div>

      <!-- Chart 3: ESG Score -->
      <div v-if="chartLoaded3" class="chart-card">
        <div class="chart-title">S&P Global ESG Score</div>
        <div class="chart-body">
          <Bar
            :chart-options="chartOptions3"
            :chart-data="chartData3"
            :height="220"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LineElement,
  LineController,
  PointElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import api from '@/api/dashboard3';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  LineElement,
  LineController,
  PointElement,
  CategoryScale,
  LinearScale
);

const moment = require('moment');

// Y축 숫자 축약 함수 (150000000 → 150M)
function formatAxisNumber(value) {
  const absVal = Math.abs(value);
  if (absVal >= 1e9) return (value / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (absVal >= 1e6) return (value / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (absVal >= 1e3) return (value / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return value;
}

export default {
  name: 'Dashboard3Mobile',
  components: { Bar },
  data: () => ({
    query: '',
    portfolio: [],
    loading: false,
    error: null,
    chartLoaded1: false,
    chartLoaded2: false,
    chartLoaded3: false,
    chartOptions1: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { left: 4, right: 4, top: 8, bottom: 4 }
      },
      plugins: {
        title: { display: false },
        legend: {
          position: 'top',
          align: 'center',
          labels: { boxWidth: 8, boxHeight: 8, font: { size: 10 }, padding: 8 }
        },
        tooltip: {
          bodyFont: { size: 11 },
          titleFont: { size: 11 }
        }
      },
      scales: {
        x: {
          ticks: {
            font: { size: 9 },
            maxRotation: 90,
            minRotation: 45,
            callback: function (label) {
              const realLabel = this.getLabelForValue(label);
              return realLabel.split(':')[1]; // year only
            },
          },
        },
        x2: {
          type: 'category',
          grid: { drawOnChartArea: false },
          ticks: {
            font: { size: 8 },
            callback: function (label) {
              const realLabel = this.getLabelForValue(label);
              const t = realLabel.split(':');
              if (t.length === 3) {
                // Abbreviate company name to 6 chars
                return t[0].length > 6 ? t[0].substring(0, 6) + '..' : t[0];
              }
              return '';
            },
          },
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          ticks: {
            font: { size: 9 },
            maxTicksLimit: 5,
            callback: (value) => formatAxisNumber(value)
          }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: {
            font: { size: 9 },
            maxTicksLimit: 5,
            callback: (value) => formatAxisNumber(value)
          }
        },
      },
    },
    chartOptions2: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { left: 4, right: 4, top: 8, bottom: 4 }
      },
      plugins: {
        title: { display: false },
        legend: {
          position: 'top',
          align: 'center',
          labels: { boxWidth: 8, boxHeight: 8, font: { size: 9 }, padding: 6 }
        },
        tooltip: {
          bodyFont: { size: 11 },
          titleFont: { size: 11 }
        }
      },
      scales: {
        x: {
          stacked: true,
          ticks: { font: { size: 9 }, maxRotation: 45 }
        },
        y: {
          stacked: true,
          ticks: {
            font: { size: 9 },
            maxTicksLimit: 5,
            callback: (value) => formatAxisNumber(value)
          }
        },
      },
    },
    chartOptions3: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { left: 4, right: 4, top: 8, bottom: 4 }
      },
      plugins: {
        title: { display: false },
        legend: {
          position: 'top',
          align: 'center',
          labels: { boxWidth: 8, boxHeight: 8, font: { size: 9 }, padding: 6 }
        },
        tooltip: {
          bodyFont: { size: 11 },
          titleFont: { size: 11 }
        }
      },
      scales: {
        x: {
          stacked: true,
          ticks: { font: { size: 9 }, maxRotation: 45 }
        },
        y: {
          stacked: true,
          ticks: { font: { size: 9 }, maxTicksLimit: 5 }
        },
      },
    },
    chartData1: {
      labels: [],
      datasets: [
        {
          label: 'Growth',
          data: [],
          borderColor: 'rgb(255, 205, 86)',
          backgroundColor: 'rgb(255, 205, 86)',
          type: 'line',
          yAxisID: 'y1',
        },
        {
          label: 'Revenue',
          data: [],
          backgroundColor: 'rgb(54, 162, 235)',
          yAxisID: 'y',
        },
      ],
    },
    chartData2: {
      labels: [],
      datasets: [
        { label: 'Scope 1', data: [], backgroundColor: 'rgb(255, 159, 64)' },
        { label: 'Scope 2', data: [], backgroundColor: 'rgb(255, 205, 86)' },
        { label: 'Scope 3 - Up', data: [], backgroundColor: 'rgb(75, 192, 192)' },
        { label: 'Scope 3 - Down', data: [], backgroundColor: 'rgb(54, 162, 235)' },
      ],
    },
    chartData3: {
      labels: [],
      datasets: [
        { label: 'Environmental', data: [], backgroundColor: 'rgb(75, 192, 192)' },
        { label: 'Social', data: [], backgroundColor: 'rgb(255, 159, 64)' },
        { label: 'Governance', data: [], backgroundColor: 'rgb(54, 162, 235)' },
      ],
    },
  }),
  mounted: async function () {
    const defaultPortfolio = ['IQ91868', 'IQ24937', 'IQ320396288'];
    try {
      defaultPortfolio.forEach((e) => {
        this.portfolio.push({ companyId: e });
      });
      await this.refresh();
    } catch (e) {
      console.error(e);
      this.error = e.message || String(e);
    }
  },
  methods: {
    truncateName(name, maxLen = 8) {
      if (!name) return '';
      return name.length > maxLen ? name.substring(0, maxLen) + '..' : name;
    },
    async addPortfolio() {
      if (!this.query) return;
      this.loading = true;
      this.error = null;
      try {
        const ret = await api.validateId(this.query);
        if (ret.errMsg != '') {
          this.error = this.query + ' is not valid: ' + ret.errMsg;
        } else {
          this.portfolio.push({ companyId: ret.companyId });
          this.query = '';
          await this.refresh();
        }
      } catch (e) {
        this.error = e.message || String(e);
      }
      this.loading = false;
    },
    removePortfolio(index) {
      this.portfolio.splice(index, 1);
      if (this.portfolio.length > 0) {
        this.renderChart();
      } else {
        this.chartLoaded1 = false;
        this.chartLoaded2 = false;
        this.chartLoaded3 = false;
      }
    },
    async refresh() {
      this.loading = true;
      this.error = null;
      try {
        for (const p of this.portfolio) {
          if (p.loaded) continue;
          const ret = await api.fetchCompanyDetail(p.companyId);
          Object.assign(p, ret);
          p.loaded = true;
        }
        await this.renderChart();
      } catch (e) {
        console.error(e);
        this.error = e.message || String(e);
      }
      this.loading = false;
    },
    async renderChart() {
      if (this.portfolio.length === 0) return;
      if (this.portfolio.filter((e) => !e.loaded).length > 0) return;
      await this.renderChart1();
      await this.renderChart2();
      await this.renderChart3();
    },
    async renderChart1() {
      this.chartLoaded1 = false;
      try {
        let r = [];
        let g = [];
        let l = [];
        for (const p of this.portfolio) {
          const ret = await api.fetchRevenues(p.companyId);
          r = r.concat(ret.revenue);
          g = g.concat(
            ret.growth.map((v, i) => {
              const k = p.name + ':' + moment(ret.perioddate[i], 'MM/DD/YYYY').format('YYYY') + (i == 1 ? ':' : '');
              return { x: k, y: v };
            })
          );
          g.push({ x: g[g.length - 1].x });
          l = l.concat(
            ret.perioddate.map(
              (e, i) => p.name + ':' + moment(e, 'MM/DD/YYYY').format('YYYY') + (i == 1 ? ':' : '')
            )
          );
        }
        this.chartData1.labels = l;
        this.chartData1.datasets[0].data = g;
        this.chartData1.datasets[1].data = r;
        this.chartLoaded1 = true;
      } catch (e) {
        console.error(e);
      }
    },
    async renderChart2() {
      this.chartLoaded2 = false;
      try {
        const institutionIds = this.portfolio.map((e) => e.institutionId).filter(Boolean);
        if (institutionIds.length === 0) return;
        const ret = await api.fetchCarbonEmissions(institutionIds, 2020);
        ret.sort((a, b) => institutionIds.indexOf(a.institutionId) - institutionIds.indexOf(b.institutionId));
        this.chartData2.labels = ret.map((e) => this.truncateName(e.institutionName, 8));
        this.chartData2.datasets[0].data = ret.map((e) => e.tcABSCrbnScope1);
        this.chartData2.datasets[1].data = ret.map((e) => e.tcABSCrbnScope2);
        this.chartData2.datasets[2].data = ret.map((e) => e.tcABSCrbnScope3);
        this.chartData2.datasets[3].data = ret.map((e) => e.tcAbsCrbnScope3Downstream);
        this.chartLoaded2 = true;
      } catch (e) {
        console.error(e);
      }
    },
    async renderChart3() {
      this.chartLoaded3 = false;
      try {
        const institutionIds = this.portfolio.map((e) => e.institutionId).filter(Boolean);
        if (institutionIds.length === 0) return;
        const ret = await api.fetchESGScore(institutionIds, 2021);
        ret.sort((a, b) => institutionIds.indexOf(a.institutionId) - institutionIds.indexOf(b.institutionId));
        this.chartData3.labels = ret.map((e) => this.truncateName(e.institutionName, 8));
        this.chartData3.datasets[0].data = ret.map(
          (e) => (e.scoreTypes[0].envScore * e.scoreTypes[0].envScoreWeight) / 100
        );
        this.chartData3.datasets[1].data = ret.map(
          (e) => (e.scoreTypes[0].socialScore * e.scoreTypes[0].socialScoreWeight) / 100
        );
        this.chartData3.datasets[2].data = ret.map(
          (e) => (e.scoreTypes[0].geScore * e.scoreTypes[0].geScoreWeight) / 100
        );
        this.chartLoaded3 = true;
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style scoped>
.mobile-container {
  padding: 8px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 13px;
}

.search {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.search input[type="text"] {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
}

.search .icon-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  font-size: 18px;
  font-weight: 600;
  border: none;
  background: #007a3f;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search .icon-btn:active {
  background: #005a2f;
}

.loader {
  text-align: center;
  color: #666;
  padding: 20px;
}

.error {
  color: #b00;
  padding: 10px;
  background: #fee;
  border-radius: 4px;
  margin-bottom: 10px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 10px 0;
  color: #333;
}

/* Portfolio Cards */
.portfolio-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.portfolio-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #d6dce4;
}

.company-name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.remove-btn {
  background: transparent;
  border: none;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  padding: 4px 8px;
}

.card-body {
  padding: 10px 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}

.info-row:last-of-type {
  border-bottom: none;
}

.info-row .label {
  color: #666;
  font-size: 12px;
}

.info-row .value {
  color: #111;
  font-size: 13px;
  text-align: right;
  max-width: 60%;
}

.info-row.highlight {
  background: #f8f9fa;
  margin: 6px -12px;
  padding: 8px 12px;
}

.info-row .score {
  font-weight: 700;
  color: #007a3f;
  font-size: 16px;
}

.pd-row {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eee;
}

.pd-item {
  text-align: center;
  flex: 1;
}

.pd-label {
  display: block;
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
}

.pd-value {
  display: block;
  font-size: 12px;
  color: #111;
  font-weight: 500;
}

/* Charts Section */
.charts-section {
  margin-top: 16px;
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 12px;
  overflow: hidden;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  padding: 10px 12px;
  background: #d6dce4;
}

.chart-body {
  padding: 8px;
}
</style>
